import { useState, useEffect, useMemo } from 'react';
import { Input } from 'src/@/components/ui/input';
import { Button } from 'src/@/components/ui/button';
import { Badge } from 'src/@/components/ui/badge';
import MenuDatas2 from 'src/components/MenuDatas2';
import moment from 'moment';
import v2_actionAnalises from 'src/actions/v2/ai/v2_actionAnalises';
import useAuth from 'src/hooks/useAuth';
import { mascararTel, validaLabelMenuDatas, verifyFeatures } from 'src/utils/functions';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import AiAnalyzeDetailsModal from 'src/sections/@dashboard/aiAnalyse/AiAnalyzeDetailsModal';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from 'src/@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import actionListaClientesCanais from 'src/actions/clientes/actionListaClientesCanais';
import { getMensagensWhatsappDb, setChatInfo } from 'src/redux/slices/chat';
import { useDispatch, useSelector } from 'react-redux';
import v2_actionBuscarChatById from 'src/actions/v2/services/chats/v2_actionBuscarChatById';
import DrawerChatWhatsapp from 'src/sections/@chats/chat/DrawerChatWhatsapp';
import { getListaUsuariosTransferencia } from 'src/redux/slices/user';
import ModalAiChat from 'src/sections/@global/ai/ModalAiChat';
import Iconify from 'src/components/Iconify';
import PageContainerHeader from 'src/components/PageContainerHeader';
import { AI_ANALISE_DESIGN, getSentimentMeta } from './AiAnaliseSentimento.design';

// No telegram chat_phone é "TG.<chat_id>", não um telefone — mascararTel()
// despe as letras e reformata os dígitos como se fosse celular brasileiro.
const formatarTelefoneAnalise = (chatPhone) => {
  if (!chatPhone) return '—';
  if (chatPhone.startsWith('TG.')) return chatPhone;
  return mascararTel(chatPhone) || '—';
};

const TABLE_HEAD = [
  { id: 'id', label: 'ID', className: 'w-32' },
  { id: 'phone', label: 'Telefone', className: 'w-40' },
  { id: 'chat_id', label: 'ID do Chat', className: 'w-40' },
  { id: 'date', label: 'Data', className: 'w-44' },
  { id: 'sentiment', label: 'Sentimento', className: 'w-44' },
  { id: 'nomeUsuario', label: 'Atendente', className: 'flex-1 min-w-[120px]' },
  { id: 'actions', label: '', className: 'w-24 text-right' },
];

export default function AiAnaliseSentimento() {
  const dispatch = useDispatch();
  const { user, cliente } = useAuth();
  const { listaUsuariosTransferencia } = useSelector((state) => state.user);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterId, setFilterId] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterChatId, setFilterChatId] = useState('');
  const [filterSentiment, setFilterSentiment] = useState('');
  const [valorEscolhidoUsuario, setValorEscolhidoUsuario] = useState(null);

  const [page, setPage] = useState(1);
  const limit = 10;

  const [inicio, setInicio] = useState(new Date());
  const [final, setFinal] = useState(new Date());
  const [periodo, setPeriodo] = useState('mes_atual');
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenuDatas = Boolean(anchorEl);

  const [loading, setLoading] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedChatId, setCopiedChatId] = useState(null);

  const [listaClientesCanais, setListaClientesCanais] = useState([]);
  const isMultisession = cliente?.multiSession === 1;
  const [clienteIdPadrao, setClienteIdPadrao] = useState(isMultisession ? null : user?.clienteId);

  const [chat, setChat] = useState(null);
  const [openChatdetalhes, setOpenChatDetalhes] = useState(false);
  const hasAccess = useMemo(
    () => verifyFeatures(cliente?.features || [], 'ai.analise_atendimento'),
    [cliente?.features]
  );

  const activeMoreFiltersCount =
    (filterSentiment ? 1 : 0) +
    (filterId ? 1 : 0) +
    (filterChatId ? 1 : 0) +
    (filterPhone ? 1 : 0);

  async function buscarListaClientesCanais() {
    try {
      if (!cliente?._id) return;
      const response_clientes_canais = await actionListaClientesCanais(cliente._id);
      setListaClientesCanais(response_clientes_canais.data.data);
      if (isMultisession && response_clientes_canais.data.data.length > 0) {
        setClienteIdPadrao(response_clientes_canais.data.data[0].cliente_id);
      }
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    buscarListaClientesCanais();
  }, []);

  const fetchAnalises = async (
    pageParam = page,
    limitParam = limit,
    periodoParam = periodo,
    inicioParam = inicio,
    finalParam = final
  ) => {
    if (!clienteIdPadrao || !hasAccess) return;
    try {
      setLoading(true);
      let querys = `?page=${pageParam}&limit=${limitParam}`;
      if (periodoParam) querys += `&periodo=${periodoParam}`;
      if (inicioParam) querys += `&inicio=${moment(inicioParam).format('YYYY-MM-DD')}`;
      if (finalParam) querys += `&final=${moment(finalParam).format('YYYY-MM-DD')}`;

      const response = await v2_actionAnalises(clienteIdPadrao, querys);
      if (response.data.error) return;
      setData(response.data.data || []);
    } catch (error) {
      console.error('Erro ao buscar análises:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalisesPorCanal = async (
    canalId,
    pageParam = page,
    limitParam = limit,
    periodoParam = periodo,
    inicioParam = inicio,
    finalParam = final
  ) => {
    if (!canalId || !hasAccess) return;
    try {
      setLoading(true);
      let querys = `?page=${pageParam}&limit=${limitParam}`;
      if (periodoParam) querys += `&periodo=${periodoParam}`;
      if (inicioParam) querys += `&inicio=${moment(inicioParam).format('YYYY-MM-DD')}`;
      if (finalParam) querys += `&final=${moment(finalParam).format('YYYY-MM-DD')}`;

      const response = await v2_actionAnalises(canalId, querys);
      if (response.data.error) return;
      setData(response.data.data || []);
    } catch (error) {
      console.error('Erro ao buscar análises:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cliente?._id) {
      dispatch(getListaUsuariosTransferencia(cliente._id));
    }
    if (clienteIdPadrao) {
      if (isMultisession) {
        fetchAnalisesPorCanal(clienteIdPadrao);
      } else {
        fetchAnalises(page, limit, periodo, inicio, final);
      }
    }
  }, [clienteIdPadrao, hasAccess]);

  const onFiltrar = async (periodoSelecionado) => {
    setPeriodo(periodoSelecionado);
    setAnchorEl(null);
    setPage(1);
    if (isMultisession) {
      await fetchAnalisesPorCanal(clienteIdPadrao, 1, limit, periodoSelecionado, inicio, final);
    } else {
      await fetchAnalises(1, limit, periodoSelecionado, inicio, final);
    }
  };

  const handleCopy = async (text, type, rowId) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'id') setCopiedId(rowId);
      if (type === 'chatId') setCopiedChatId(rowId);
      setTimeout(() => {
        setCopiedId(null);
        setCopiedChatId(null);
      }, 2000);
    } catch (err) {}
  };

  const handleApplyFilters = () => {
    let filtered = data || [];
    if (filterId) {
      const regex = new RegExp(filterId, 'i');
      filtered = filtered.filter((item) => regex.test(item._id));
    }
    if (filterPhone) {
      const regex = new RegExp(filterPhone, 'i');
      filtered = filtered.filter((item) => regex.test(item.chat_phone));
    }
    if (filterChatId) {
      const regex = new RegExp(filterChatId, 'i');
      filtered = filtered.filter((item) => regex.test(item.chat_id));
    }
    if (filterSentiment) {
      const regex = new RegExp(filterSentiment, 'i');
      filtered = filtered.filter((item) => regex.test(String(item.result?.sentiment)));
    }
    if (valorEscolhidoUsuario) {
      filtered = filtered.filter((item) => item.user_id === valorEscolhidoUsuario);
    }
    setFilteredData(filtered);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    if (isMultisession) {
      fetchAnalisesPorCanal(clienteIdPadrao, newPage, limit);
    } else {
      fetchAnalises(newPage, limit);
    }
  };

  async function handleOpenChat(chatId) {
    setOpenChatDetalhes(true);
    const res = await v2_actionBuscarChatById(chatId, null, user.id);
    setChat(res.data);
    dispatch(setChatInfo(res.data));
    dispatch(getMensagensWhatsappDb(res.data.sessao, res.data.numeroCliente));
  }

  useEffect(() => {
    handleApplyFilters();
  }, [filterId, filterPhone, filterChatId, filterSentiment, valorEscolhidoUsuario, data]);

  const onReload = () => {
    if (!clienteIdPadrao) return;
    if (isMultisession) {
      fetchAnalisesPorCanal(clienteIdPadrao);
    } else {
      fetchAnalises(page, limit, periodo, inicio, final);
    }
  };

  const visibleRows = filteredData.slice((page - 1) * limit, (page - 1) * limit + limit);

  return (
    <>
      {chat && (
        <DrawerChatWhatsapp
          chat={chat}
          open={openChatdetalhes}
          onClose={() => {
            setChat(null);
            setOpenChatDetalhes(false);
          }}
          listaFunil={[]}
          source={'chat.supervisionar'}
        />
      )}

      <div className="h-full flex flex-col">
        <PageContainerHeader
          titleSection={
            <div className="flex flex-wrap items-center gap-4 flex-1">
              {/* Identificação */}
              <div className="flex items-center gap-2 min-w-fit">
                <div className="p-2 bg-[#7C3AED]/10 rounded-lg">
                  <Iconify icon="mdi:brain" className="text-[#7C3AED]" width={20} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Análise de Sentimento</h3>
                </div>
              </div>

              <div className="h-8 w-px bg-gray-200/60 hidden xl:block mx-2" />

              {/* Filtros */}
              <div className="flex flex-wrap items-end gap-2 flex-1">
                <div className="w-[200px]">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block pl-1">
                    Período
                  </span>
                  <MenuDatas2
                    anchorEl={anchorEl}
                    open={openMenuDatas}
                    onClose={() => setAnchorEl(false)}
                    inicial={inicio}
                    final={final}
                    content={validaLabelMenuDatas(periodo, inicio, final)}
                    customButton={
                      <Button
                        variant="outline"
                        className="w-full h-9 justify-between text-xs font-normal border-dashed border-gray-300 bg-transparent hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-violet-50 text-gray-700"
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                      >
                        <span className="truncate">{validaLabelMenuDatas(periodo, inicio, final)}</span>
                        <Iconify icon="mdi:calendar" className="ml-2 h-3.5 w-3.5 opacity-50" />
                      </Button>
                    }
                    changeInicial={(newValue) => setInicio(newValue)}
                    changeFinal={(newValue) => setFinal(newValue)}
                    onSelect={(value) => onFiltrar(value)}
                    onPesquisar={() => onFiltrar(periodo)}
                  />
                </div>

                {isMultisession && (
                  <div className="w-[200px]">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block pl-1">
                      Canal
                    </span>
                    <Select
                      value={clienteIdPadrao || 'all'}
                      onValueChange={(e) => {
                        const value = e === 'all' ? null : e;
                        setClienteIdPadrao(value);
                        if (value) fetchAnalisesPorCanal(value);
                      }}
                    >
                      <SelectTrigger className="h-9 text-xs border-gray-200 bg-white text-gray-900 focus:ring-[#7C3AED]/20">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {listaClientesCanais.map((item) => (
                          <SelectItem key={item.cliente_id} value={item.cliente_id}>
                            {item.nome || 'undefined'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Botão Mais Filtros */}
                <div className="flex items-end">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-9 px-3 border-dashed border-gray-300 text-xs text-gray-600 hover:border-violet-500 hover:text-violet-600 hover:bg-violet-50 bg-transparent"
                      >
                        <Iconify icon="mdi:filter-plus" className="mr-2 h-4 w-4" />
                        Mais filtros
                        {(!!valorEscolhidoUsuario ||
                          !!filterSentiment ||
                          !!filterId ||
                          !!filterChatId ||
                          !!filterPhone) && (
                          <span className="ml-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-100 text-[10px] font-medium text-violet-800">
                            {(!!valorEscolhidoUsuario ? 1 : 0) +
                              (!!filterSentiment ? 1 : 0) +
                              (!!filterId ? 1 : 0) +
                              (!!filterChatId ? 1 : 0) +
                              (!!filterPhone ? 1 : 0)}
                          </span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-4" align="start">
                      <div className="space-y-4">
                        <h4 className="font-medium text-sm text-gray-900 border-b border-gray-200 pb-2">
                          Filtros Adicionais
                        </h4>

                        <div className="space-y-3">
                          {/* Atendente */}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                              Atendente
                            </label>
                            <Select
                              value={valorEscolhidoUsuario || 'all'}
                              onValueChange={(e) =>
                                setValorEscolhidoUsuario(e === 'all' ? null : e)
                              }
                            >
                              <SelectTrigger className="h-9 text-xs w-full border-gray-200 bg-white text-gray-900 focus:ring-[#7C3AED]/20">
                                <SelectValue placeholder="Todos" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {listaUsuariosTransferencia.map((item) => (
                                  <SelectItem key={item._id} value={item._id}>
                                    {item.firstName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Sentimento */}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                              Sentimento
                            </label>
                            <Select
                              value={filterSentiment || 'all'}
                              onValueChange={(e) => setFilterSentiment(e === 'all' ? '' : e)}
                            >
                              <SelectTrigger className="h-9 text-xs w-full border-gray-200 bg-white text-gray-900 focus:ring-[#7C3AED]/20">
                                <SelectValue placeholder="Todos" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {[
                                  { value: '5', label: '5 · Muito satisfeito' },
                                  { value: '4', label: '4 · Satisfeito' },
                                  { value: '3', label: '3 · Neutro' },
                                  { value: '2', label: '2 · Insatisfeito' },
                                  { value: '1', label: '1 · Muito insatisfeito' },
                                ].map((item) => (
                                  <SelectItem key={item.value} value={item.value}>
                                    {item.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* ID da análise */}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                              ID da análise
                            </label>
                            <Input
                              placeholder="Pesquisar por ID"
                              value={filterId}
                              onChange={(e) => setFilterId(e.target.value)}
                              className="h-9 text-xs bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-lg"
                            />
                          </div>

                          {/* ID do chat */}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                              ID do chat
                            </label>
                            <Input
                              placeholder="Pesquisar por chat ID"
                              value={filterChatId}
                              onChange={(e) => setFilterChatId(e.target.value)}
                              className="h-9 text-xs bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-lg"
                            />
                          </div>

                          {/* Telefone */}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                              Telefone
                            </label>
                            <Input
                              placeholder="Pesquisar por telefone"
                              value={filterPhone}
                              onChange={(e) => setFilterPhone(e.target.value)}
                              className="h-9 text-xs bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          }
          rightSection={
            <div className="flex items-center gap-2 w-full md:w-auto justify-end">
              <ModalAiChat
                triggerLabel="Análise com IA"
                buttonDisabled={!clienteIdPadrao || filteredData.length === 0}
                dataSendAi={{
                  cliente_id: clienteIdPadrao,
                  ids: filteredData.map((i) => i._id),
                  periodo,
                  inicio: moment(inicio).format('YYYY-MM-DD'),
                  final: moment(final).format('YYYY-MM-DD'),
                }}
              />
              <Button
                className="h-9 w-9 p-0 rounded-lg border-dashed border-gray-300 text-gray-600 hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-[#7C3AED]/5"
                variant="outline"
                onClick={onReload}
                title="Recarregar"
              >
                <Iconify icon="mdi:reload" width={18} />
              </Button>
            </div>
          }
        />

        <div className="w-[98%] mx-auto flex flex-col flex-1 min-h-0 gap-3 pb-3">
          {/* Loading bar */}
          {loading && (
            <div className={AI_ANALISE_DESIGN.loadingBar.track}>
              <div className={AI_ANALISE_DESIGN.loadingBar.bar} style={{ width: '40%' }} />
            </div>
          )}

          {/* Tabela */}
          <div className="relative flex-1 min-h-0 flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {!hasAccess && (
            <div className={AI_ANALISE_DESIGN.locked.overlay}>
              <div className={AI_ANALISE_DESIGN.locked.card}>
                <div className={AI_ANALISE_DESIGN.locked.iconBox}>
                  <Iconify icon="mdi:lock-outline" width={32} className="text-[#7C3AED]" />
                </div>
                <h3 className={AI_ANALISE_DESIGN.locked.title}>
                  Ative o módulo de análise de atendimento
                </h3>
                <p className={AI_ANALISE_DESIGN.locked.description}>
                  Esta análise detalhada é exclusiva do módulo{' '}
                  <strong>Análise de atendimento</strong>. Ative agora para acessar resumos, análise
                  de sentimento e recomendações estratégicas da IA.
                </p>
                <p className="text-xs text-slate-500">
                  Entre em contato com o suporte para mais informações.
                </p>
              </div>
            </div>
          )}

          {/* Header da tabela (sticky) */}
          <div className="border-b border-gray-100 bg-gray-50/80 backdrop-blur-sm px-6 py-3 flex items-center text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">
            {TABLE_HEAD.map((head) => (
              <div key={head.id} className={head.className || ''}>
                {head.label}
              </div>
            ))}
          </div>

          {/* Body scrollável */}
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200">
            {loading && visibleRows.length === 0 ? (
              <div className="px-6 py-16 text-center">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Iconify
                    icon="eos-icons:loading"
                    width={24}
                    className="animate-spin text-gray-400"
                  />
                  <span className="text-sm text-gray-400">Carregando análises...</span>
                </div>
              </div>
            ) : visibleRows.length === 0 ? (
              <div className={AI_ANALISE_DESIGN.emptyState.container}>
                <div className={AI_ANALISE_DESIGN.emptyState.iconBox}>
                  <Iconify
                    icon="mdi:feature-search-outline"
                    width={28}
                    className="text-gray-400"
                  />
                </div>
                <p className={AI_ANALISE_DESIGN.emptyState.title}>Nenhuma análise encontrada</p>
                <p className={AI_ANALISE_DESIGN.emptyState.description}>
                  Tente ajustar o período ou os filtros aplicados.
                </p>
              </div>
            ) : (
              visibleRows.map((row) => {
                const meta = getSentimentMeta(row?.result?.sentiment);
                const sentimentValue = parseInt(row?.result?.sentiment, 10);
                return (
                  <div
                    key={row._id}
                    className={AI_ANALISE_DESIGN.table.row}
                    onClick={() => {
                      setDetailsRow(row);
                      setOpenDetails(true);
                    }}
                  >
                    {/* ID */}
                    <div className="w-32 pr-3">
                      <div className={AI_ANALISE_DESIGN.table.copyChip}>
                        <span className={AI_ANALISE_DESIGN.table.copyChipText}>
                          {row._id.slice(0, 8)}
                        </span>
                        <button
                          type="button"
                          className={AI_ANALISE_DESIGN.table.copyButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(row._id, 'id', row._id);
                          }}
                          title="Copiar ID"
                        >
                          <Iconify
                            icon={copiedId === row._id ? 'eva:checkmark-outline' : 'eva:copy-outline'}
                            width={13}
                            className={copiedId === row._id ? 'text-green-600' : ''}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Telefone */}
                    <div className="w-40 pr-3 text-sm text-gray-700">
                      {formatarTelefoneAnalise(row.chat_phone)}
                    </div>

                    {/* Chat ID */}
                    <div className="w-40 pr-3">
                      <div className={AI_ANALISE_DESIGN.table.copyChip}>
                        <span className={AI_ANALISE_DESIGN.table.copyChipText}>
                          {(row.chat_id || '').slice(0, 8)}
                        </span>
                        <button
                          type="button"
                          className={AI_ANALISE_DESIGN.table.copyButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(row.chat_id, 'chatId', row._id);
                          }}
                          title="Copiar ID do chat"
                        >
                          <Iconify
                            icon={
                              copiedChatId === row._id
                                ? 'eva:checkmark-outline'
                                : 'eva:copy-outline'
                            }
                            width={13}
                            className={copiedChatId === row._id ? 'text-green-600' : ''}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Data */}
                    <div className="w-44 pr-3 text-sm text-gray-600">
                      {row.date
                        ? format(new Date(row.date), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                        : '—'}
                    </div>

                    {/* Sentimento */}
                    <div className="w-44 pr-3">
                      <Badge
                        variant="outline"
                        className={`${meta.badge} text-xs font-medium flex items-center gap-1.5 w-fit border`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                        {Number.isFinite(sentimentValue) ? `${sentimentValue} · ${meta.short}` : 'N/A'}
                      </Badge>
                    </div>

                    {/* Atendente */}
                    <div className="flex-1 min-w-[120px] pr-3 text-sm text-gray-700 truncate">
                      {row.nomeUsuario || '—'}
                    </div>

                    {/* Ações */}
                    <div className="w-24 flex items-center justify-end gap-1">
                      <button
                        type="button"
                        className={AI_ANALISE_DESIGN.table.actionButtonView}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailsRow(row);
                          setOpenDetails(true);
                        }}
                        title="Ver detalhes da análise"
                      >
                        <Iconify icon="eva:eye-outline" width={16} />
                      </button>
                      <button
                        type="button"
                        className={AI_ANALISE_DESIGN.table.actionButtonChat}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenChat(row.chat_id);
                        }}
                        title="Ir para o chat"
                      >
                        <Iconify icon="eva:message-circle-outline" width={16} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {(() => {
            const totalCount = filteredData.length;
            const totalPages = Math.max(1, Math.ceil(totalCount / limit));
            const from = totalCount === 0 ? 0 : (page - 1) * limit + 1;
            const to = Math.min(page * limit, totalCount);
            const isFirst = page <= 1;
            const isLast = page >= totalPages;
            return (
              <div className="bg-white border-t border-gray-100 px-4 py-2.5 flex items-center justify-end gap-3 text-xs text-gray-500 flex-shrink-0">
                <span className="tabular-nums">
                  {from}–{to} de {totalCount}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={isFirst}
                    onClick={() => handleChangePage(page - 1)}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500 transition-colors"
                    aria-label="Página anterior"
                  >
                    <Iconify icon="eva:chevron-left-outline" width={16} />
                  </button>
                  <button
                    type="button"
                    disabled={isLast}
                    onClick={() => handleChangePage(page + 1)}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500 transition-colors"
                    aria-label="Próxima página"
                  >
                    <Iconify icon="eva:chevron-right-outline" width={16} />
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        </div>

        <AiAnalyzeDetailsModal
          open={openDetails}
          onClose={() => setOpenDetails(false)}
          data={detailsRow}
          onOpenChat={(chatId) => {
            setOpenDetails(false);
            handleOpenChat(chatId);
          }}
        />
      </div>
    </>
  );
}
